import { useDisclosure, Flex, Box, Text } from "@chakra-ui/react";
import { TrashIcon } from "@digital-services-gard-as/anchor-icons";
import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import SavedStatuses from "./SaveStatus/SavedStatuses";
import { Button, Heading } from "@digital-services-gard-as/anchor-react";
import {
    updateInvoiceByPutMutationOptions,
    deleteInvoiceMutationOptions
} from "@/api/queryOptions/invoiceAPIQueryOptions";
import useAutosave from "@/app/invoice/[invoiceId]/_components/createInvoice/header/useAutosave.hook";
import { useAutosaveFeedback } from "@/app/invoice/[invoiceId]/_components/createInvoice/header/useAutosaveFeedback.hook";
import { useInvoiceDeleteFeedback } from "@/app/invoice/[invoiceId]/_components/createInvoice/header/useInvoiceDeleteFeedback.hook";
import { getBranchCompanyNameFromSession, useInvoiceEventRedirect, StatusBadge } from "@/utils/invoiceUtils";
import { DeleteInvoiceModal } from "./DeleteInvoiceModal";
import { InvoiceDetailsDto, BankAccountDto } from "@/types";

type InvoiceFormHeaderProps = {
    invoiceId: string;
    invoiceStatus: InvoiceDetailsDto["status"];
    isInvoiceInSubmission: boolean;
    isFormChanged: boolean;
    clearFormChangedIfCurrent: (savedVersion: number) => void;
    formChangeVersionRef: React.RefObject<number>;
    bankAccounts: BankAccountDto[];
};

export const InvoiceFormHeader = ({
    invoiceId,
    invoiceStatus,
    isInvoiceInSubmission,
    isFormChanged,
    clearFormChangedIfCurrent,
    formChangeVersionRef,
    bankAccounts
}: InvoiceFormHeaderProps) => {
    const { t } = useTranslation(["invoices", "errorMessages"]);
    const lastSavedRef = useRef<number>(Date.now());
    const branchCompanyName = getBranchCompanyNameFromSession();
    const navigate = useNavigate();
    const { broadcast } = useInvoiceEventRedirect(navigate);

    const { open: isDeleteModalOpen, onOpen: onOpenDeleteModal, onClose: onCloseDeleteModal } = useDisclosure();
    const cancelRef = useRef<HTMLButtonElement | null>(null);

    const {
        mutate: updateInvoice,
        isPending: isUpdatingInvoice,
        isSuccess: isUpdateInvoiceSuccess,
        isError: isUpdateInvoiceError
    } = useMutation(updateInvoiceByPutMutationOptions);

    useAutosave({
        isFormChanged,
        bankAccounts,
        lastSavedRef,
        invoiceId,
        clearFormChangedIfCurrent,
        formChangeVersionRef,
        isInvoiceInSubmission,
        updateInvoice,
        isUpdatingInvoice
    });

    useAutosaveFeedback({
        isUpdateInvoiceError,
        isUpdateInvoiceSuccess
    });

    const {
        mutate: deleteInvoice,
        isSuccess: isDeleteInvoiceSuccess,
        isError: isDeleteInvoiceError
    } = useMutation(deleteInvoiceMutationOptions);

    useInvoiceDeleteFeedback({
        isDeleteInvoiceSuccess,
        isDeleteInvoiceError,
        broadcast,
        navigate,
        t,
        invoiceId
    });

    return (
        <>
            <DeleteInvoiceModal
                cancelRef={cancelRef}
                deleteInvoice={deleteInvoice}
                invoiceId={invoiceId}
                isDeleteModalOpen={isDeleteModalOpen}
                onCloseDeleteModal={onCloseDeleteModal}
            />
            <Flex w="100%" justifyContent="space-between" alignItems="center" px="1rem" py="0.5rem">
                <Flex alignItems="center" gap="1rem">
                    <Heading size="xl" id="createInvoiceTitle">
                        {t("invoices:createInvoice.title")}
                    </Heading>
                    <Box mt="0.4rem">
                        <StatusBadge id="statusBadge" size="lg" invoiceStatus={invoiceStatus} />
                    </Box>
                </Flex>
                <BranchCompanyInfo name={branchCompanyName} label={t("invoices:branchCompany")} />
                <Flex alignItems="center" gap="1rem">
                    <SavedStatuses
                        isSaving={isUpdatingInvoice}
                        isSavingError={isUpdateInvoiceError}
                        isNotSaved={isFormChanged && !isUpdatingInvoice && !isUpdateInvoiceError}
                        lastSavedRef={lastSavedRef}
                    />
                    <Button id="deleteInvoiceBtn" variant="secondary" icon={<TrashIcon />} onClick={onOpenDeleteModal}>
                        {t("invoices:deleteInvoice")}
                    </Button>
                </Flex>
            </Flex>
        </>
    );
};

const BranchCompanyInfo = ({ name, label }: { name?: string; label: string }) => {
    if (!name) {
        return null;
    }
    return (
        <Flex alignItems="center" flexDir="column" justifyContent="center" mt="1.5rem">
            <Text data-testid="branchCompanyTitle" asChild>
                <b>{label}</b>
            </Text>
            <Text data-testid="branchCompany">{name}</Text>
        </Flex>
    );
};
